import React, { useState } from "react";
import axios from "axios";
import { Calendar, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { authHeader } from "@/utils/userAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CONSULTATION_PACKAGES = [
  { id: "rapido", icon: "⚡", title: "Chiarimento Rapido", duration: "30 minuti", desc: "Per risolvere dubbi specifici e ricevere indicazioni immediate." },
  { id: "strategia", icon: "🎯", title: "Strategia Personalizzata", duration: "90 minuti", desc: "Analizziamo il caso completo per definire insieme cosa fare." },
  { id: "affiancamento", icon: "🤝", title: "Affiancamento Strategico", duration: "4 settimane", desc: "Per applicare e correggere la strategia con supporto continuo." },
];

/**
 * Popup riutilizzabile per richiedere informazioni su una consulenza.
 * Usato sia dentro la Lezione 1 (auto-popup a 50%) che dalla Members Area / Dashboard.
 *
 * @param {boolean} open
 * @param {object} user — { name, email, phone }
 * @param {() => void} onClose
 * @param {() => void} onSubmitted - chiamato dopo il successo
 * @param {(eventName: string, extra?: object) => void} fireTrack - opzionale, per tracking custom
 * @param {string} variant - "lesson" | "standalone" (testo footer cambia)
 */
export default function ConsultationDialog({ open, user, onClose, onSubmitted, fireTrack, variant = "standalone" }) {
  const [note, setNote] = useState("");
  const [packageId, setPackageId] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!packageId) { setError("Seleziona prima il pacchetto che ti interessa."); return; }
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/consultation/book`, { package_id: packageId, preferred_slot_note: note.trim() || null }, { headers: authHeader() });
      fireTrack && fireTrack("popup_consultation_submitted", { package_id: packageId });
      onSubmitted && onSubmitted();
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Errore invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent data-testid="consultation-dialog" className="sm:max-w-[560px] rounded-2xl border-0 p-0 overflow-hidden bg-white max-h-[90vh] overflow-y-auto">
        <div className="h-2 bg-tension w-full" />
        <div className="px-7 pt-6 pb-7">
          <DialogHeader className="space-y-2 text-left">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-1" style={{ background: "var(--np-quaternary)" }}>
              <Calendar size={20} style={{ color: "var(--np-secondary)" }} />
            </div>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
              Richiedi info su una consulenza
            </DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
              Scegli il pacchetto che ti interessa: il team di Alessandro Fulgheri ti contatterà entro 24 ore lavorative per fornirti tutti i dettagli e concordare i prossimi passi.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="space-y-2.5" data-testid="consultation-packages">
              {CONSULTATION_PACKAGES.map((p) => {
                const selected = packageId === p.id;
                return (
                  <button
                    key={p.id} type="button" onClick={() => setPackageId(p.id)}
                    data-testid={`pkg-${p.id}`}
                    className={`w-full text-left rounded-xl border-2 p-3.5 transition flex items-start gap-3 ${selected ? "shadow-[0_15px_40px_-20px_rgba(217,94,57,0.45)]" : "hover:bg-[#fafcfe]"}`}
                    style={{ borderColor: selected ? "var(--np-secondary)" : "#dbe3ec", background: selected ? "#fff8f4" : "#fff" }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <p className="text-base font-bold" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>{p.title}</p>
                        <span className="text-[11px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full" style={{ background: selected ? "var(--np-secondary)" : "#f0f4f8", color: selected ? "#fff" : "var(--np-muted)" }}>
                          {p.duration}
                        </span>
                      </div>
                      <p className="text-[13px] mt-1 leading-relaxed" style={{ color: "var(--np-muted)" }}>{p.desc}</p>
                    </div>
                    <span className={`w-5 h-5 shrink-0 mt-1 rounded-full border-2 flex items-center justify-center`} style={{ borderColor: selected ? "var(--np-secondary)" : "#cbd5e0", background: selected ? "var(--np-secondary)" : "transparent" }}>
                      {selected && <span className="block w-2 h-2 rounded-full bg-white" />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="rounded-lg border border-[#e3eaf2] bg-[#f7fafd] p-3 text-[13px]" style={{ color: "var(--np-muted)" }}>
              I tuoi dati sono già nel sistema:<br/>
              <strong style={{ color: "var(--np-primary)" }}>{user?.name}</strong> · {user?.email} · {user?.phone}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="note" className="text-[13px] font-semibold tracking-wide uppercase" style={{ color: "var(--np-primary)" }}>
                Preferenze sull&apos;orario o domande <span className="font-normal normal-case text-[12px]" style={{ color: "var(--np-muted)" }}>(facoltativo)</span>
              </Label>
              <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Es. preferisco la mattina dopo le 9, oppure la sera dopo le 18"
                className="rounded-lg border-[#dbe3ec] focus-visible:ring-[var(--np-secondary)] resize-none" />
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

            <div className="flex flex-wrap gap-3 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-full text-sm font-semibold border" style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
                {variant === "lesson" ? "Forse più tardi" : "Chiudi"}
              </button>
              <button type="submit" disabled={sending || !packageId} data-testid="consultation-submit" className="cta-btn flex-1 disabled:opacity-50">
                {sending ? (<><Loader2 className="animate-spin" size={16} /> Invio…</>) : (<>Richiedi info</>)}
              </button>
            </div>

            {variant === "lesson" && (
              <p className="text-[11px] flex items-start gap-1.5 pt-1" style={{ color: "var(--np-muted)" }}>
                <ShieldCheck size={12} className="mt-0.5" style={{ color: "var(--np-secondary)" }} />
                Il video riprende automaticamente dopo l&apos;invio.
              </p>
            )}
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
